import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { PhoneOff, ArrowRight, Loader2, Info } from 'lucide-react';
import { toast } from 'sonner';
import { executeBigQuery, updateMSN } from '../utils/bigQueryClient';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

interface OptOutFormFrProps {
  nanoidValue?: string | null;
}

const OptOutFormFr: React.FC<OptOutFormFrProps> = ({ nanoidValue }) => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [msisdn, setMsisdn] = useState<string | null>(null);
  const [clientIP, setClientIP] = useState<string>('');
  const [currentNanoid, setCurrentNanoid] = useState<string>('');
  const [showDebugInfo, setShowDebugInfo] = useState(false);
  const [requestPayload, setRequestPayload] = useState<any>(null);
  const [responseDetails, setResponseDetails] = useState<any>(null);
  const [debugLog, setDebugLog] = useState<string[]>([]);
  const navigate = useNavigate();
  const location = useLocation();

  const logDebug = (message: string) => {
    console.log(message);
    setDebugLog(prev => [...prev, `${new Date().toISOString()}: ${message}`]);
  };

  useEffect(() => {
    const getClientIP = async () => {
      try {
        const response = await fetch('https://api.ipify.org?format=json');
        const data = await response.json();
        setClientIP(data.ip);
        logDebug(`Client IP set: ${data.ip}`);
      } catch (error) {
        console.error('Error fetching IP address:', error);
        setClientIP('unknown');
        logDebug(`Error getting IP: ${error instanceof Error ? error.message : String(error)}`);
      }
    };

    getClientIP();
  }, []);

  useEffect(() => {
    const fetchContactData = async () => {
      try {
        let nanoid = nanoidValue;
        
        if (!nanoid) {
          const queryParams = new URLSearchParams(location.search);
          nanoid = queryParams.get('t');
        }
        
        if (!nanoid) {
          logDebug('No nanoid value found');
          setIsLoading(false);
          return;
        }

        setCurrentNanoid(nanoid);
        logDebug(`Using nanoid for BigQuery: ${nanoid}`);

        const query = `
          SELECT MSISDN 
          FROM \`lottosocial-9f7a2.customerSync.syncBulkSMSfromIntegrate\`
          WHERE nanoid = @nanoid
          LIMIT 1
        `;
        
        const results = await executeBigQuery(query, [nanoid]);
        
        if (results && results.length > 0) {
          const msnValue = results[0].MSISDN;
          setMsisdn(msnValue);
          logDebug(`MSISDN found: ${msnValue}`);
          
          if (msnValue) {
            setPhoneNumber(formatPhoneNumber(msnValue));
          }
        } else {
          logDebug('No MSISDN found for the given nanoid');
        }
      } catch (error) {
        console.error('Error fetching contact data:', error);
        logDebug(`Error fetching contact: ${error instanceof Error ? error.message : String(error)}`);
        toast.error('Erreur lors de la récupération des données de contact');
      } finally {
        setIsLoading(false);
      }
    };

    fetchContactData();
  }, [location.search, nanoidValue]);

  const formatPhoneNumber = (value: string) => {
    if (!value) return '';
    
    let digits = value.replace(/\D/g, '');
    
    if (digits.startsWith('33') && digits.length > 2) {
      digits = digits.substring(2);
    }
    
    if (digits.length === 0) {
      return '';
    } else if (digits.length <= 1) {
      return `+33 ${digits}`;
    } else if (digits.length <= 3) {
      return `+33 ${digits.slice(0, 1)} ${digits.slice(1)}`;
    } else if (digits.length <= 5) {
      return `+33 ${digits.slice(0, 1)} ${digits.slice(1, 3)} ${digits.slice(3)}`;
    } else if (digits.length <= 7) {
      return `+33 ${digits.slice(0, 1)} ${digits.slice(1, 3)} ${digits.slice(3, 5)} ${digits.slice(5)}`;
    } else if (digits.length <= 9) {
      return `+33 ${digits.slice(0, 1)} ${digits.slice(1, 3)} ${digits.slice(3, 5)} ${digits.slice(5, 7)} ${digits.slice(7)}`;
    } else {
      return `+33 ${digits.slice(0, 1)} ${digits.slice(1, 3)} ${digits.slice(3, 5)} ${digits.slice(5, 7)} ${digits.slice(7, 9)}`;
    }
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    
    if (!inputValue || inputValue.trim() === '' || inputValue.replace(/\s+/g, '') === '+33') {
      setPhoneNumber('');
      return;
    }
    
    if (inputValue.length <= 4 && phoneNumber.startsWith('+33')) {
      if (phoneNumber.length > inputValue.length) {
        setPhoneNumber('');
        return;
      }
    }
    
    const formattedNumber = formatPhoneNumber(inputValue);
    setPhoneNumber(formattedNumber);
  };

  const preparePayload = () => {
    const rawPhoneNumber = phoneNumber.replace(/\D/g, '');
    
    const payload = {
      phone_number: rawPhoneNumber,
      nanoid: currentNanoid
    };
    
    setRequestPayload(payload);
    logDebug(`Payload prepared: ${JSON.stringify(payload)}`);
    return payload;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const digitsOnly = phoneNumber.replace(/\D/g, '');
    
    const isValid = digitsOnly.length === 10 || 
                   (digitsOnly.length === 11 && digitsOnly.startsWith('33'));
    
    if (!isValid) {
      toast.error('Veuillez saisir un numéro de téléphone valide');
      return;
    }
    
    setIsSubmitting(true);
    setResponseDetails(null);
    
    try {
      const payload = preparePayload();
      
      setShowDebugInfo(true);
      
      logDebug('Updating MSN field in BigQuery...');
      logDebug(`Current nanoid: ${currentNanoid}, Phone: ${payload.phone_number}`);
      
      try {
        await updateMSN(currentNanoid, payload.phone_number);
        
        logDebug('Successfully updated MSN in BigQuery');
        setResponseDetails({
          method: 'BigQuery Update',
          status: 'Success',
          data: { message: 'MSN field updated in BigQuery' }
        });
        
        navigate('/confirmation-fr', { state: { phoneNumber } });
      } catch (error) {
        const errMsg = error instanceof Error ? error.message : String(error);
        logDebug(`Error updating MSN in BigQuery: ${errMsg}`);
        setResponseDetails({
          method: 'BigQuery Update',
          status: 'Failed',
          error: errMsg
        });
        throw new Error(`Erreur de mise à jour: ${errMsg}`);
      }
    } catch (error) {
      console.error('Error in submission:', error);
      const errMsg = error instanceof Error ? error.message : 'Une erreur est survenue. Veuillez réessayer.';
      logDebug(`Final error: ${errMsg}`);
      toast.error(errMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="w-full max-w-md mx-auto flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-optout-blue" />
        <span className="ml-2 text-optout-text">Chargement...</span>
      </div>
    );
  }

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="glass-card rounded-2xl p-8 animate-fade-in">
        <div className="flex justify-center mb-6">
          <div className="bg-optout-lightblue p-3 rounded-full">
            <PhoneOff className="h-6 w-6 text-optout-blue" />
          </div>
        </div>
        
        <h2 className="text-2xl font-semibold text-center mb-2 text-optout-text">
          Désabonnement des Messages SMS
        </h2>
        
        <p className="text-optout-lighttext text-center mb-6 text-balance">
          Entrez votre numéro de téléphone ci-dessous pour arrêter de recevoir des messages marketing par SMS.
        </p>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <label htmlFor="phone" className="block text-sm font-medium text-optout-text">
              Numéro de Téléphone
            </label>
            <input
              id="phone"
              type="tel"
              placeholder="+33 6 55 55 55 55"
              value={phoneNumber}
              onChange={handlePhoneChange}
              className="glass-input w-full px-4 py-3 rounded-lg text-optout-text placeholder:text-gray-400"
              disabled={isSubmitting}
              required
              autoComplete="tel"
              maxLength={17}
            />
            {msisdn && (
              <p className="text-xs text-optout-blue mt-1">
                MSISDN identifié: {msisdn}
              </p>
            )}
          </div>
          
          <button
            type="submit"
            disabled={isSubmitting}
            className="btn-primary w-full py-3 px-4 rounded-lg flex items-center justify-center space-x-2"
            onClick={() => {
              if (!isSubmitting) {
                preparePayload();
                setShowDebugInfo(true);
              }
            }}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                <span>Traitement...</span>
              </>
            ) : (
              <>
                <span>Se Désabonner</span>
                <ArrowRight className="h-5 w-5" />
              </>
            )}
          </button>
        </form>
        
        <p className="text-xs text-optout-lighttext text-center mt-6">
          Votre demande sera traitée dans un délai de 48 Heures.
        </p>
        
        <div className="mt-6">
          <button 
            onClick={() => setShowDebugInfo(!showDebugInfo)} 
            className="flex items-center text-xs text-optout-blue"
          >
            <Info className="h-3 w-3 mr-1" />
            {showDebugInfo ? 'Masquer' : 'Afficher'} les informations de débogage
          </button>
          
          {showDebugInfo && (
            <div className="mt-2 p-3 bg-gray-100 rounded-lg text-xs font-mono overflow-auto max-h-60">
              <h4 className="font-semibold mb-1">Données à envoyer:</h4>
              <pre>{JSON.stringify(requestPayload, null, 2)}</pre>
              
              {responseDetails && (
                <>
                  <h4 className="font-semibold mt-3 mb-1">Résultat de la dernière soumission:</h4>
                  <div className={`p-2 rounded ${responseDetails.status === 'Success' ? 'bg-green-100' : 'bg-red-100'}`}>
                    <p>Méthode: {responseDetails.method}</p>
                    <p>Status: {responseDetails.status}</p>
                    {responseDetails.data && (
                      <pre>{JSON.stringify(responseDetails.data, null, 2)}</pre>
                    )}
                    {responseDetails.error && (
                      <pre className="text-red-600">{JSON.stringify(responseDetails.error, null, 2)}</pre>
                    )}
                  </div>
                </>
              )}
              
              <h4 className="font-semibold mt-3 mb-1">Informations sur la connexion:</h4>
              <div>
                <p>IP Client: {clientIP}</p>
                <p>Nanoid: {currentNanoid || 'Non spécifié'}</p>
                <p>MSISDN: {msisdn || 'Non trouvé'}</p>
              </div>
              
              <h4 className="font-semibold mt-3 mb-1">Journal de débogage:</h4>
              <div className="bg-black text-green-400 p-2 rounded text-xs">
                {debugLog.map((log, idx) => (
                  <div key={idx}>{log}</div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      
      <div className="flex items-center justify-center mt-6 space-x-2 animate-fade-in" style={{ animationDelay: '200ms' }}>
        <div className="h-1 w-1 rounded-full bg-optout-blue opacity-50"></div>
        <div className="h-1 w-1 rounded-full bg-optout-blue opacity-70"></div>
        <div className="h-1 w-1 rounded-full bg-optout-blue"></div>
        <div className="h-1 w-1 rounded-full bg-optout-blue opacity-70"></div>
        <div className="h-1 w-1 rounded-full bg-optout-blue opacity-50"></div>
      </div>
    </div>
  );
};

export default OptOutFormFr;
